import { useRouteShellContext } from "@components/routeShellContext"
import { sanitizeRichHtml } from "@lib/sanitizeHtml"
import * as m from "@paraglide/messages"
import type { BrandBackgroundVars, BrandsProps } from "@site-types/components/brands"
import type { ContentImage } from "@site-types/content"
import type { CSSProperties } from "react"
import { imageRenderProps, imageUrl } from "../../componentUtils"
import Footer from "../../Footer"
import OptimizedImage from "../../OptimizedImage"
import "./styles.css"

function backgroundVar(source?: string) {
  return source ? `url("${source}")` : undefined
}

export function Brands({ brandImage, settings }: BrandsProps) {
  const { locale } = useRouteShellContext()
  const backgroundSources = {
    taiyoro: imageUrl(settings.homePageTaiyoroBackground as ContentImage | undefined, "workCover"),
    playdata: imageUrl(settings.homePagePlaydataBackground as ContentImage | undefined, "workCover"),
    flux: imageUrl(settings.homePageFluxBackground as ContentImage | undefined, "workCover"),
  }
  const backgroundVars: BrandBackgroundVars = {
    "--background-taiyoro": backgroundVar(backgroundSources.taiyoro),
    "--background-playdata": backgroundVar(backgroundSources.playdata),
    "--background-flux": backgroundVar(backgroundSources.flux),
  }
  const brands = [
    {
      className: "futaroku",
      backgroundSrc: undefined,
      href: m.home_brands_futaroku_href({}, { locale: locale }),
      imgSrc: settings.homePageFutarokuLogo as ContentImage | undefined,
      imgAlt: m.home_brands_futaroku({}, { locale: locale }),
      tagLine: m.home_brands_futaroku_tagLine({}, { locale: locale }).toUpperCase(),
      blurb: m.home_brands_futaroku_blurb({}, { locale: locale }),
    },
    {
      className: "taiyoro",
      backgroundSrc: backgroundSources.taiyoro,
      href: m.home_brands_taiyoro_href({}, { locale: locale }),
      imgSrc: settings.homePageTaiyoroLogo as ContentImage | undefined,
      imgAlt: m.home_brands_taiyoro({}, { locale: locale }),
      tagLine: m.home_brands_taiyoro_tagLine({}, { locale: locale }).toUpperCase(),
      blurb: m.home_brands_taiyoro_blurb({}, { locale: locale }),
    },
    {
      className: "playdata",
      backgroundSrc: backgroundSources.playdata,
      href: m.home_brands_playdata_href({}, { locale: locale }),
      imgSrc: settings.homePagePlaydataLogo as ContentImage | undefined,
      imgAlt: m.home_brands_playdata({}, { locale: locale }),
      tagLine: m.home_brands_playdata_tagLine({}, { locale: locale }).toUpperCase(),
      blurb: m.home_brands_playdata_blurb({}, { locale: locale }),
    },
    {
      className: "flux",
      backgroundSrc: backgroundSources.flux,
      href: m.home_brands_flux_href({}, { locale: locale }),
      imgSrc: settings.homePageFluxLogo as ContentImage | undefined,
      imgAlt: m.home_brands_flux({}, { locale: locale }),
      tagLine: m.home_brands_flux_tagLine({}, { locale: locale }).toUpperCase(),
      blurb: m.home_brands_flux_blurb({}, { locale: locale }),
    },
  ]

  return (
    <section
      className="brands scroll-area"
      id="brands"
      style={backgroundVars as CSSProperties}
    >
      <div className="brands-container">
        <div className="playbrain brand">
          <span className="logo">
            {brandImage ? (
              <OptimizedImage
                className="symbol"
                alt="PlayBrain"
                {...imageRenderProps(brandImage, "logo")}
              />
            ) : null}
          </span>
          <span className="body">
            <span
              className="blurb"
              dangerouslySetInnerHTML={{
                __html: sanitizeRichHtml(m.home_brands_description({}, { locale: locale })),
              }}
            />
            <a
              href={`/${locale}/work`}
              className="learn-more"
            >
              {m.home_brands_button({}, { locale: locale })}
            </a>
          </span>
        </div>
        {brands.map((brand) => (
          <a
            key={brand.className}
            className={`${brand.className} brand brand-${brand.className}`}
            href={brand.href}
            target="_blank"
            rel="noreferrer"
            data-remote-background-src={brand.backgroundSrc}
          >
            <span className="logo">
              {brand.imgSrc ? (
                <OptimizedImage
                  alt={brand.imgAlt}
                  {...imageRenderProps(brand.imgSrc, "logo")}
                />
              ) : null}
            </span>
            <span className="body">
              <strong>{locale === "ja" ? brand.tagLine.toLocaleLowerCase() : brand.tagLine}</strong>
              <span className="blurb">{brand.blurb}</span>
            </span>
          </a>
        ))}
      </div>
      <Footer />
    </section>
  )
}

export default Brands
